import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    Receipt, CreditCard, LayoutGrid, Crown,
    CheckCircle, XCircle, RotateCcw, Building2,
    FileText, Calendar
} from 'lucide-react';

function BillingOverview({ user }) {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('taxed'); // 'taxed' | 'exempt'

    const userId = user?.companyId || user?.uid;

    useEffect(() => {
        if (userId) {
            loadPayments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const loadPayments = async () => {
        try {
            setLoading(true);
            const paymentsRef = collection(db, 'subscriptionPayments');
            const paymentsQuery = query(
                paymentsRef,
                where('userId', '==', userId),
                orderBy('paidAt', 'desc'),
                limit(50)
            );

            const snapshot = await getDocs(paymentsQuery);
            const paymentsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setPayments(paymentsList);
        } catch (error) {
            console.error('결제 내역 로드 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredPayments = payments.filter(p => p.billingType === activeTab);

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const formatAmount = (amount) => {
        if (!amount && amount !== 0) return '-';
        return amount.toLocaleString() + '원';
    };

    const getTypeLabel = (subscriptionType) => {
        switch (subscriptionType) {
            case 'membership': return '멤버십';
            case 'app_usage': return '앱 이용료';
            case 'labFee': return '기공료';
            default: return subscriptionType || '기타';
        }
    };

    const getTypeIcon = (subscriptionType) => {
        switch (subscriptionType) {
            case 'membership':
                return <Crown size={20} color="#6366f1" />;
            case 'app_usage':
                return <LayoutGrid size={20} color="#6366f1" />;
            case 'labFee':
                return <CreditCard size={20} color="#6366f1" />;
            default:
                return <CreditCard size={20} color="#6366f1" />;
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'completed':
                return {
                    label: '완료',
                    icon: <CheckCircle size={14} />,
                    style: styles.statusCompleted
                };
            case 'failed':
                return {
                    label: '실패',
                    icon: <XCircle size={14} />,
                    style: styles.statusFailed
                };
            case 'refunded':
                return {
                    label: '환불',
                    icon: <RotateCcw size={14} />,
                    style: styles.statusRefunded
                };
            default:
                return {
                    label: status || '대기',
                    icon: null,
                    style: styles.statusDefault
                };
        }
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <Receipt size={48} color="#6366f1" />
                <p style={styles.loadingText}>결제 내역을 불러오는 중...</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* 헤더 */}
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <Receipt size={28} color="#6366f1" />
                    <h1 style={styles.title}>청구 내역</h1>
                </div>
            </div>

            {/* 탭 선택 */}
            <div style={styles.tabContainer}>
                <button
                    onClick={() => setActiveTab('taxed')}
                    style={{
                        ...styles.tab,
                        ...(activeTab === 'taxed' ? styles.tabActive : {})
                    }}
                >
                    <Building2 size={18} />
                    과세 (덴탈클러스터)
                    {activeTab === 'taxed' && <div style={styles.tabIndicator} />}
                </button>
                <button
                    onClick={() => setActiveTab('exempt')}
                    style={{
                        ...styles.tab,
                        ...(activeTab === 'exempt' ? styles.tabActive : {})
                    }}
                >
                    <Building2 size={18} />
                    면세 (아이반)
                    {activeTab === 'exempt' && <div style={styles.tabIndicator} />}
                </button>
            </div>

            {/* 탭 헤더 정보 */}
            <div style={styles.billingInfoCard}>
                <div style={styles.billingInfoIcon}>
                    <FileText size={24} color="#6366f1" />
                </div>
                <div style={styles.billingInfoContent}>
                    {activeTab === 'taxed' ? (
                        <>
                            <h3 style={styles.billingInfoEntity}>덴탈클러스터</h3>
                            <p style={styles.billingInfoDesc}>앱 이용료 (과세) | 증빙: 세금계산서</p>
                        </>
                    ) : (
                        <>
                            <h3 style={styles.billingInfoEntity}>아이반 치과기공소</h3>
                            <p style={styles.billingInfoDesc}>멤버십 비용 + 기공료 (면세) | 증빙: 계산서</p>
                        </>
                    )}
                </div>
                <div style={styles.billingInfoCount}>
                    <span style={styles.billingInfoCountNumber}>{filteredPayments.length}</span>
                    <span style={styles.billingInfoCountLabel}>건</span>
                </div>
            </div>

            {/* 결제 내역 카드 리스트 */}
            {filteredPayments.length === 0 ? (
                <div style={styles.emptyState}>
                    <Receipt size={64} color="#cbd5e1" />
                    <p style={styles.emptyText}>결제 내역이 없습니다</p>
                    <p style={styles.emptySubText}>
                        {activeTab === 'taxed'
                            ? '앱 이용료 결제 내역이 여기에 표시됩니다.'
                            : '멤버십 및 기공료 결제 내역이 여기에 표시됩니다.'
                        }
                    </p>
                </div>
            ) : (
                <div style={styles.paymentList}>
                    {filteredPayments.map(payment => {
                        const statusBadge = getStatusBadge(payment.status);
                        return (
                            <div key={payment.id} style={styles.paymentCard}>
                                <div style={styles.paymentCardLeft}>
                                    <div style={styles.paymentIcon}>
                                        {getTypeIcon(payment.subscriptionType)}
                                    </div>
                                    <div style={styles.paymentInfo}>
                                        <span style={styles.paymentType}>
                                            {getTypeLabel(payment.subscriptionType)}
                                        </span>
                                        <span style={styles.paymentDate}>
                                            <Calendar size={14} color="#94a3b8" />
                                            {formatDate(payment.paidAt)}
                                        </span>
                                    </div>
                                </div>
                                <div style={styles.paymentCardRight}>
                                    <span style={styles.paymentAmount}>
                                        {formatAmount(payment.amount)}
                                    </span>
                                    <span style={statusBadge.style}>
                                        {statusBadge.icon}
                                        {statusBadge.label}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

const styles = {
    container: {
        padding: '32px',
        maxWidth: '1400px',
        margin: '0 auto',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        gap: '16px',
    },
    loadingText: {
        fontSize: '16px',
        color: '#64748b',
        fontWeight: '500',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px',
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#1e293b',
        margin: 0,
    },
    tabContainer: {
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        backgroundColor: '#f1f5f9',
        borderRadius: '16px',
        padding: '6px',
    },
    tab: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '14px 20px',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '12px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#64748b',
        cursor: 'pointer',
        transition: 'all 0.2s',
        position: 'relative',
    },
    tabActive: {
        backgroundColor: '#ffffff',
        color: '#6366f1',
        boxShadow: '0 2px 8px rgba(99, 102, 241, 0.15)',
    },
    tabIndicator: {
        position: 'absolute',
        bottom: '6px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '32px',
        height: '3px',
        backgroundColor: '#6366f1',
        borderRadius: '2px',
    },
    billingInfoCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '24px',
        backgroundColor: '#f5f3ff',
        borderRadius: '16px',
        border: '2px solid #e0e7ff',
        marginBottom: '24px',
    },
    billingInfoIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '48px',
        height: '48px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        border: '2px solid #e0e7ff',
    },
    billingInfoContent: {
        flex: 1,
    },
    billingInfoEntity: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#1e293b',
        margin: '0 0 4px 0',
    },
    billingInfoDesc: {
        fontSize: '14px',
        color: '#6366f1',
        margin: 0,
        fontWeight: '500',
    },
    billingInfoCount: {
        display: 'flex',
        alignItems: 'baseline',
        gap: '4px',
    },
    billingInfoCountNumber: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#6366f1',
    },
    billingInfoCountLabel: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#94a3b8',
    },
    paymentList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    paymentCard: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 24px',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        border: '2px solid #e2e8f0',
        transition: 'all 0.2s',
    },
    paymentCardLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    paymentIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '44px',
        height: '44px',
        backgroundColor: '#f5f3ff',
        borderRadius: '12px',
    },
    paymentInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    paymentType: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
    },
    paymentDate: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '13px',
        color: '#94a3b8',
    },
    paymentCardRight: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '6px',
    },
    paymentAmount: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#1e293b',
    },
    statusCompleted: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 10px',
        backgroundColor: '#dcfce7',
        color: '#16a34a',
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: '600',
    },
    statusFailed: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 10px',
        backgroundColor: '#fef2f2',
        color: '#dc2626',
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: '600',
    },
    statusRefunded: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 10px',
        backgroundColor: '#fff7ed',
        color: '#ea580c',
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: '600',
    },
    statusDefault: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 10px',
        backgroundColor: '#f1f5f9',
        color: '#64748b',
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: '600',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 20px',
        backgroundColor: '#ffffff',
        borderRadius: '20px',
        border: '2px solid #e2e8f0',
    },
    emptyText: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#94a3b8',
        marginTop: '16px',
        marginBottom: '8px',
    },
    emptySubText: {
        fontSize: '14px',
        color: '#cbd5e1',
        margin: 0,
    },
};

export default BillingOverview;
